/**
 * Harvest & Settlement — Farmer-facing cockpit
 *
 * Sections:
 *   1. Crop Cycle Summary  — DOC progress, stocking date, harvest window, biomass
 *   2. Pre-Harvest Test     — PHT status, upload/view report, schedule button
 *   3. Procurement Details  — lot size, rate/kg, total sale, payment status
 *   4. Settlement Waterfall — sale proceeds → deductions → farmer profit
 *
 * All data from existing APIs.  No backend modifications.
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  LinearProgress,
  Divider,
  Tooltip,
  IconButton,
} from '@mui/material';
import {
  Sell as SellIcon,
  Refresh,
  Agriculture,
  Science,
  LocalShipping,
  AccountBalance,
  CheckCircle,
  Schedule,
  Warning,
  TrendingUp,
  AttachMoney,
  WaterDrop,
  CalendarMonth,
  Scale,
  Receipt,
  ArrowDownward,
  ArrowRight,
  Info as InfoIcon,
} from '@mui/icons-material';
import { useAuth } from 'hooks/useAuth';
import {
  stockingEventsAPI,
  harvestsAPI,
  settlementAPI,
  creditAPI,
  escrowAPI,
  farmsAPI,
  pondsAPI,
  labReportsAPI,
} from 'services/api';
import DOCProgressIndicator from 'shared/components/DOCProgressIndicator';

// ─── Helpers ────────────────────────────────────────────────
const fmt = (v) =>
  parseFloat(v || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : '—');

const HARVEST_STATUS_CFG = {
  planned:      { label: 'Planned',      color: 'info',    icon: <Schedule fontSize="small" /> },
  in_progress:  { label: 'In Progress',  color: 'warning', icon: <Agriculture fontSize="small" /> },
  completed:    { label: 'Completed',    color: 'success', icon: <CheckCircle fontSize="small" /> },
  cancelled:    { label: 'Cancelled',    color: 'default', icon: <Warning fontSize="small" /> },
  partial:      { label: 'Partial',      color: 'warning', icon: <Warning fontSize="small" /> },
};

const SETTLEMENT_STATUS_CFG = {
  pending:    { label: 'Pending',    color: 'warning' },
  processing: { label: 'Processing', color: 'info'    },
  completed:  { label: 'Completed',  color: 'success' },
  disputed:   { label: 'Disputed',   color: 'error'   },
  cancelled:  { label: 'Cancelled',  color: 'default' },
};

const PHT_STATUS = {
  NOT_SCHEDULED: 'not_scheduled',
  SCHEDULED:     'scheduled',
  COMPLETED:     'completed',
};

// Estimate harvest window: stocking date + 90–130 days
const estimateHarvestWindow = (stockingDate, targetDOC = 120) => {
  if (!stockingDate) return null;
  const sd = new Date(stockingDate);
  const earlyDate = new Date(sd.getTime() + 90 * 86400000);
  const lateDate = new Date(sd.getTime() + targetDOC * 86400000);
  return { early: earlyDate, late: lateDate };
};

const calculateDOC = (stockingDate) => {
  if (!stockingDate) return 0;
  return Math.floor((new Date() - new Date(stockingDate)) / 86400000);
};

// ═════════════════════════════════════════════════════════════
// Main Component
// ═════════════════════════════════════════════════════════════
const HarvestSettlementPage = () => {
  const { user } = useAuth();

  // Data state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stockingEvents, setStockingEvents] = useState([]);
  const [harvests, setHarvests] = useState([]);
  const [settlements, setSettlements] = useState([]);
  const [creditApps, setCreditApps] = useState([]);
  const [ledgerBalance, setLedgerBalance] = useState(null);
  const [repaymentSchedule, setRepaymentSchedule] = useState(null);
  const [farms, setFarms] = useState([]);
  const [ponds, setPonds] = useState([]);
  const [phtReports, setPhtReports] = useState([]);

  // Derived primary objects
  const [activeStocking, setActiveStocking] = useState(null);
  const [latestHarvest, setLatestHarvest] = useState(null);
  const [latestSettlement, setLatestSettlement] = useState(null);
  const [activeCreditApp, setActiveCreditApp] = useState(null);

  // ── Load data ─────────────────────────────────────────────
  const loadData = useCallback(async () => {
    if (!user) return;
    try {
      setLoading(true);
      setError(null);

      // Parallel fetch
      const [
        stockRes,
        harvestRes,
        settlementRes,
        creditRes,
        farmsRes,
      ] = await Promise.allSettled([
        stockingEventsAPI.getAll(),
        harvestsAPI.getAll(),
        settlementAPI.getAll(),
        creditAPI.getApplications(),
        farmsAPI.getAll(),
      ]);

      // Stocking events
      const stockData = stockRes.status === 'fulfilled'
        ? (Array.isArray(stockRes.value?.data) ? stockRes.value.data : stockRes.value || [])
        : [];
      setStockingEvents(stockData);

      // Find most recent active stocking (sort by date desc, pick first ongoing)
      const sorted = [...stockData].sort(
        (a, b) => new Date(b.stocking_date) - new Date(a.stocking_date),
      );
      const active = sorted[0] || null;
      setActiveStocking(active);

      // Harvests
      const harvestData = harvestRes.status === 'fulfilled'
        ? (Array.isArray(harvestRes.value?.data) ? harvestRes.value.data : harvestRes.value || [])
        : [];
      setHarvests(harvestData);
      const lh = harvestData[0] || null; // already sorted desc from backend
      setLatestHarvest(lh);

      // Settlements
      const settlementData = settlementRes.status === 'fulfilled'
        ? (Array.isArray(settlementRes.value?.data) ? settlementRes.value.data : settlementRes.value || [])
        : [];
      setSettlements(settlementData);
      setLatestSettlement(settlementData[0] || null);

      // Credit applications
      const creditData = creditRes.status === 'fulfilled'
        ? (creditRes.value?.data || [])
        : [];
      setCreditApps(creditData);
      const activeLoan = creditData.find((a) => a.status === 'disbursed')
        || creditData.find((a) => a.status === 'approved')
        || creditData[0] || null;
      setActiveCreditApp(activeLoan);

      // Farms + ponds
      const farmsData = farmsRes.status === 'fulfilled'
        ? (farmsRes.value?.data || [])
        : [];
      setFarms(farmsData);

      // Load ponds for all farms
      if (farmsData.length > 0) {
        const pondResults = await Promise.allSettled(
          farmsData.map((f) => pondsAPI.getByFarm(f.id)),
        );
        const allPonds = pondResults
          .filter((r) => r.status === 'fulfilled')
          .flatMap((r) => (Array.isArray(r.value?.data) ? r.value.data : r.value || []));
        setPonds(allPonds);
      }

      // Load lab reports for the active stocking's pond (PHT proxy)
      if (active?.pond_id) {
        try {
          const labRes = await labReportsAPI.getByPond(active.pond_id, { testType: 'pre_harvest' });
          const labData = Array.isArray(labRes?.data) ? labRes.data : labRes || [];
          setPhtReports(labData);
        } catch {
          setPhtReports([]);
        }
      }

      // Ledger balance + repayment for active credit
      if (activeLoan) {
        await Promise.allSettled([
          escrowAPI
            .getLedgerBalance(activeLoan.id)
            .then((r) => setLedgerBalance(r.data || null))
            .catch(() => setLedgerBalance(null)),
          creditAPI
            .getRepaymentSchedule(activeLoan.id)
            .then((r) => setRepaymentSchedule(r.data || null))
            .catch(() => setRepaymentSchedule(null)),
        ]);
      }
    } catch (err) {
      console.error('Harvest page load error:', err);
      setError('Failed to load harvest & settlement data.');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // ── Derived values ────────────────────────────────────────
  const doc = activeStocking ? calculateDOC(activeStocking.stocking_date) : 0;
  const targetDOC = 120;
  const harvestWindow = activeStocking
    ? estimateHarvestWindow(activeStocking.stocking_date, targetDOC)
    : null;

  // Biomass estimate: stocking density × pond area × avg survival × avg weight
  const activePond = ponds.find((p) => p.id === activeStocking?.pond_id);
  const pondAreaM2 = activePond ? parseFloat(activePond.area || activePond.pond_area || 0) * 10000 : 0; // ha to m²
  const stockDensity = activeStocking?.stocking_density_per_m2 || activeStocking?.stocking_density || 0;
  const estimatedCount = pondAreaM2 * stockDensity;
  const survivalRate = 0.80; // 80% default
  const avgWeightG = latestHarvest?.average_size_grams || (doc > 60 ? doc * 0.25 : 15);
  const biomassKg = (estimatedCount * survivalRate * avgWeightG) / 1000;

  // PHT status
  const phtStatus = phtReports.length > 0
    ? (phtReports.some((r) => r.result_status === 'completed' || r.status === 'completed')
      ? PHT_STATUS.COMPLETED
      : PHT_STATUS.SCHEDULED)
    : PHT_STATUS.NOT_SCHEDULED;

  const phtEligible = doc >= 80; // eligible after DOC 80

  // Settlement waterfall display
  const saleProceeds = latestHarvest?.total_value || 0;
  const principalDue = repaymentSchedule?.principal_amount || activeCreditApp?.approved_amount || 0;
  const interestDue = repaymentSchedule?.interest_amount || 0;
  const insurancePremium = latestSettlement?.insurance_premium || 0;
  const totalDeductions = principalDue + interestDue + insurancePremium;
  const farmerProfit = saleProceeds - totalDeductions;

  // Crop stage
  const getCropStage = () => {
    if (!activeStocking) return 'no_crop';
    if (latestSettlement && latestSettlement.status === 'completed') return 'settled';
    if (latestHarvest && latestHarvest.status === 'completed') return 'harvested';
    if (doc >= 100) return 'harvest_ready';
    if (doc >= 80) return 'pht_eligible';
    if (doc >= 45) return 'growing';
    if (doc >= 0) return 'early';
    return 'no_crop';
  };
  const cropStage = getCropStage();

  const STAGE_CFG = {
    no_crop:       { label: 'No Active Crop',   color: 'default', progress: 0 },
    early:         { label: 'Early Growth',      color: 'info',    progress: 15 },
    growing:       { label: 'Growing',           color: 'primary', progress: 40 },
    pht_eligible:  { label: 'PHT Eligible',      color: 'warning', progress: 65 },
    harvest_ready: { label: 'Harvest Ready',     color: 'success', progress: 85 },
    harvested:     { label: 'Harvested',         color: 'success', progress: 95 },
    settled:       { label: 'Settled',           color: 'default', progress: 100 },
  };

  const stageCfg = STAGE_CFG[cropStage] || STAGE_CFG.no_crop;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1, sm: 3 } }}>
      {/* ── Page Header ─────────────────────────────── */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <SellIcon sx={{ color: '#2196f3', fontSize: 32 }} />
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              Harvest &amp; Settlement
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Crop cycle tracking, procurement, and settlement waterfall
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Chip
            label={stageCfg.label}
            color={stageCfg.color}
            size="small"
            sx={{ fontWeight: 600 }}
          />
          <Button variant="outlined" size="small" startIcon={<Refresh />} onClick={loadData}>
            Refresh
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* ═══════════════════════════════════════════════════════
          SECTION 1 — Crop Cycle Summary
          ═══════════════════════════════════════════════════════ */}
      <Card sx={{ mb: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider', overflow: 'visible' }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Agriculture color="primary" />
            Crop Cycle Summary
          </Typography>

          {!activeStocking ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Agriculture sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
              <Typography variant="body1" color="text.secondary">
                No active stocking event found.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Create a stocking event in the Ponds section to start tracking your crop cycle.
              </Typography>
            </Box>
          ) : (
            <Grid container spacing={3}>
              {/* DOC Progress */}
              <Grid item xs={12} md={4}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    bgcolor: 'primary.50',
                    border: '1px solid',
                    borderColor: 'primary.100',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <DOCProgressIndicator
                    stockingDate={activeStocking.stocking_date}
                    targetDOC={targetDOC}
                    variant="circular"
                    size="small"
                    showDetails
                    label="Days of Culture"
                  />
                </Box>
              </Grid>

              {/* Key metrics */}
              <Grid item xs={12} md={8}>
                <Grid container spacing={2}>
                  <Grid item xs={6} sm={3}>
                    <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 2, textAlign: 'center' }}>
                      <CalendarMonth sx={{ fontSize: 24, color: 'info.main', mb: 0.5 }} />
                      <Typography variant="caption" color="text.secondary" display="block">
                        Stocking Date
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {fmtDate(activeStocking.stocking_date)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 2, textAlign: 'center' }}>
                      <WaterDrop sx={{ fontSize: 24, color: 'primary.main', mb: 0.5 }} />
                      <Typography variant="caption" color="text.secondary" display="block">
                        Species
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {activeStocking.species || 'Vannamei'}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 2, textAlign: 'center' }}>
                      <Scale sx={{ fontSize: 24, color: 'success.main', mb: 0.5 }} />
                      <Typography variant="caption" color="text.secondary" display="block">
                        Biomass Est.
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: 'success.main' }}>
                        {biomassKg > 0 ? `${fmt(biomassKg)} kg` : '—'}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 2, textAlign: 'center' }}>
                      <Schedule sx={{ fontSize: 24, color: 'warning.main', mb: 0.5 }} />
                      <Typography variant="caption" color="text.secondary" display="block">
                        Harvest Window
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {harvestWindow
                          ? `${harvestWindow.early.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })} – ${harvestWindow.late.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}`
                          : '—'}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>

                {/* Crop stage timeline bar */}
                <Box sx={{ mt: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      Crop Stage Progress
                    </Typography>
                    <Chip label={stageCfg.label} size="small" color={stageCfg.color} sx={{ height: 20, fontSize: '0.65rem' }} />
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={stageCfg.progress}
                    sx={{
                      height: 10,
                      borderRadius: 5,
                      bgcolor: 'grey.200',
                      '& .MuiLinearProgress-bar': {
                        borderRadius: 5,
                        background:
                          cropStage === 'harvest_ready' || cropStage === 'harvested'
                            ? 'linear-gradient(90deg, #4caf50, #66bb6a)'
                            : cropStage === 'pht_eligible'
                              ? 'linear-gradient(90deg, #ff9800, #ffa726)'
                              : 'linear-gradient(90deg, #2196f3, #42a5f5)',
                      },
                    }}
                  />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5, opacity: 0.6 }}>
                    <Typography variant="caption">Stocking</Typography>
                    <Typography variant="caption">Growth</Typography>
                    <Typography variant="caption">PHT</Typography>
                    <Typography variant="caption">Harvest</Typography>
                    <Typography variant="caption">Settle</Typography>
                  </Box>
                </Box>

                {/* Additional info */}
                <Box sx={{ display: 'flex', gap: 2, mt: 2, flexWrap: 'wrap' }}>
                  {activePond && (
                    <Chip
                      variant="outlined"
                      size="small"
                      label={`Pond: ${activePond.name || activePond.pond_name || `#${activePond.id}`}`}
                    />
                  )}
                  {activeStocking.stocking_density_per_m2 && (
                    <Chip
                      variant="outlined"
                      size="small"
                      label={`Density: ${activeStocking.stocking_density_per_m2}/m²`}
                    />
                  )}
                  {activeStocking.spf_seed && (
                    <Chip size="small" label="SPF Seed" color="success" icon={<CheckCircle />} />
                  )}
                </Box>
              </Grid>
            </Grid>
          )}
        </CardContent>
      </Card>

      {/* ═══════════════════════════════════════════════════════
          SECTION 2 — Pre-Harvest Test (PHT)
          ═══════════════════════════════════════════════════════ */}
      {activeStocking && (
        <Card sx={{ mb: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Science color="secondary" />
                Pre-Harvest Test (PHT)
              </Typography>
              <Chip
                label={
                  phtStatus === PHT_STATUS.COMPLETED
                    ? 'Completed'
                    : phtStatus === PHT_STATUS.SCHEDULED
                      ? 'Scheduled'
                      : 'Not Scheduled'
                }
                color={
                  phtStatus === PHT_STATUS.COMPLETED
                    ? 'success'
                    : phtStatus === PHT_STATUS.SCHEDULED
                      ? 'info'
                      : 'default'
                }
                icon={
                  phtStatus === PHT_STATUS.COMPLETED
                    ? <CheckCircle />
                    : phtStatus === PHT_STATUS.SCHEDULED
                      ? <Schedule />
                      : undefined
                }
                sx={{ fontWeight: 600 }}
              />
            </Box>

            {phtStatus === PHT_STATUS.NOT_SCHEDULED ? (
              <Box sx={{ textAlign: 'center', py: 3 }}>
                <Science sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                <Typography variant="body1" color="text.secondary" gutterBottom>
                  No pre-harvest test scheduled.
                </Typography>
                {phtEligible ? (
                  <>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Your crop is at DOC {doc} — eligible for a pre-harvest test.
                    </Typography>
                    <Tooltip title="Contact your AIC officer to schedule a pre-harvest test">
                      <Button variant="contained" color="secondary" startIcon={<Science />}>
                        Schedule PHT
                      </Button>
                    </Tooltip>
                  </>
                ) : (
                  <Alert severity="info" sx={{ mt: 1, display: 'inline-flex', borderRadius: 2 }}>
                    PHT is available after DOC 80. Current DOC: {doc}.
                  </Alert>
                )}
              </Box>
            ) : (
              <Box>
                {/* PHT reports list */}
                <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'grey.50' }}>
                        <TableCell sx={{ fontWeight: 600 }}>Report ID</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Test Type</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Result</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {phtReports.map((report) => (
                        <TableRow key={report.id} hover>
                          <TableCell>
                            <Typography variant="body2" fontWeight={600}>
                              #{report.id}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                              {(report.test_type || 'pre_harvest').replace(/_/g, ' ')}
                            </Typography>
                          </TableCell>
                          <TableCell>{fmtDate(report.report_date || report.created_at)}</TableCell>
                          <TableCell>
                            <Chip
                              label={(report.result_status || report.status || 'pending').replace(/_/g, ' ')}
                              size="small"
                              color={
                                (report.result_status || report.status) === 'completed'
                                  ? 'success'
                                  : (report.result_status || report.status) === 'abnormal'
                                    ? 'error'
                                    : 'warning'
                              }
                              sx={{ textTransform: 'capitalize' }}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {report.summary || report.notes || '—'}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                {phtStatus === PHT_STATUS.COMPLETED && (
                  <Alert severity="success" sx={{ mt: 2, borderRadius: 2 }}>
                    <Typography variant="body2">
                      <strong>PHT Complete.</strong> Your crop has passed the pre-harvest test and is ready for procurement.
                    </Typography>
                  </Alert>
                )}
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {/* ═══════════════════════════════════════════════════════
          SECTION 3 — Procurement Details
          ═══════════════════════════════════════════════════════ */}
      <Card sx={{ mb: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <LocalShipping color="info" />
            Procurement Details
          </Typography>

          {harvests.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 3 }}>
              <LocalShipping sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
              <Typography variant="body1" color="text.secondary" gutterBottom>
                No harvest recorded yet.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Procurement details will appear once a harvest event is recorded.
              </Typography>
            </Box>
          ) : (
            <>
              {/* Summary cards for latest harvest */}
              {latestHarvest && (
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={6} sm={3}>
                    <Card variant="outlined" sx={{ borderRadius: 2, height: '100%' }}>
                      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                        <Typography variant="caption" color="text.secondary">Lot Size</Typography>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                          {fmt(latestHarvest.total_quantity_kg)} kg
                        </Typography>
                        {latestHarvest.live_quantity_kg && (
                          <Typography variant="caption" color="success.main">
                            Live: {fmt(latestHarvest.live_quantity_kg)} kg
                          </Typography>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Card variant="outlined" sx={{ borderRadius: 2, height: '100%' }}>
                      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                        <Typography variant="caption" color="text.secondary">Rate / kg</Typography>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                          ₹{latestHarvest.market_price_per_kg ? fmt(latestHarvest.market_price_per_kg) : '—'}
                        </Typography>
                        {latestHarvest.average_size_grams && (
                          <Typography variant="caption" color="text.secondary">
                            Avg size: {fmt(latestHarvest.average_size_grams)}g
                          </Typography>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Card variant="outlined" sx={{ borderRadius: 2, height: '100%' }}>
                      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                        <Typography variant="caption" color="text.secondary">Total Sale Value</Typography>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: 'success.main' }}>
                          ₹{fmt(latestHarvest.total_value)}
                        </Typography>
                        {latestHarvest.quality_grade && (
                          <Chip
                            label={`Grade: ${latestHarvest.quality_grade}`}
                            size="small"
                            color={
                              latestHarvest.quality_grade === 'A' ? 'success'
                                : latestHarvest.quality_grade === 'B' ? 'info'
                                  : 'warning'
                            }
                            sx={{ mt: 0.5 }}
                          />
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Card variant="outlined" sx={{ borderRadius: 2, height: '100%' }}>
                      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                        <Typography variant="caption" color="text.secondary">Payment Status</Typography>
                        {latestSettlement ? (
                          <>
                            <Box sx={{ mt: 0.5 }}>
                              <Chip
                                label={(SETTLEMENT_STATUS_CFG[latestSettlement.payment_status] || SETTLEMENT_STATUS_CFG[latestSettlement.status] || { label: latestSettlement.payment_status || latestSettlement.status || 'Unknown' }).label}
                                color={(SETTLEMENT_STATUS_CFG[latestSettlement.payment_status] || SETTLEMENT_STATUS_CFG[latestSettlement.status] || { color: 'default' }).color}
                                sx={{ fontWeight: 600 }}
                              />
                            </Box>
                            {latestSettlement.paid_amount > 0 && (
                              <Typography variant="caption" color="success.main" sx={{ mt: 0.5, display: 'block' }}>
                                Paid: ₹{fmt(latestSettlement.paid_amount)}
                              </Typography>
                            )}
                          </>
                        ) : (
                          <Typography variant="h6" sx={{ fontWeight: 700, color: 'warning.main' }}>
                            Pending
                          </Typography>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              )}

              {/* Harvest history table */}
              {harvests.length > 1 && (
                <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'grey.50' }}>
                        <TableCell sx={{ fontWeight: 600 }}>Harvest #</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>Qty (kg)</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>Rate/kg</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>Total Value</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {harvests.map((h) => {
                        const cfg = HARVEST_STATUS_CFG[h.status] || HARVEST_STATUS_CFG.planned;
                        return (
                          <TableRow key={h.id} hover>
                            <TableCell>
                              <Typography variant="body2" fontWeight={600}>
                                {h.harvest_number || `#${h.id}`}
                              </Typography>
                            </TableCell>
                            <TableCell>{fmtDate(h.harvest_date)}</TableCell>
                            <TableCell align="right">{fmt(h.total_quantity_kg)}</TableCell>
                            <TableCell align="right">
                              {h.market_price_per_kg ? `₹${fmt(h.market_price_per_kg)}` : '—'}
                            </TableCell>
                            <TableCell align="right">
                              {h.total_value ? `₹${fmt(h.total_value)}` : '—'}
                            </TableCell>
                            <TableCell>
                              <Chip label={cfg.label} color={cfg.color} size="small" icon={cfg.icon} />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* ═══════════════════════════════════════════════════════
          SECTION 4 — Settlement Waterfall
          ═══════════════════════════════════════════════════════ */}
      <Card sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <AccountBalance color="primary" />
            Settlement Waterfall
          </Typography>

          {!latestHarvest && !activeCreditApp ? (
            <Box sx={{ textAlign: 'center', py: 3 }}>
              <AccountBalance sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
              <Typography variant="body1" color="text.secondary" gutterBottom>
                Settlement details will appear after harvest and loan data are available.
              </Typography>
            </Box>
          ) : (
            <Grid container spacing={3}>
              {/* Waterfall breakdown */}
              <Grid item xs={12} md={7}>
                <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
                  {/* Sale Proceeds */}
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      p: 2,
                      bgcolor: '#e8f5e9',
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Receipt sx={{ color: 'success.main' }} />
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        Sale Proceeds
                      </Typography>
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: 'success.main' }}>
                      ₹{fmt(saleProceeds)}
                    </Typography>
                  </Box>

                  {/* Deduction: Principal */}
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      p: 2,
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <ArrowDownward sx={{ color: 'error.main', fontSize: 20 }} />
                      <Typography variant="body2" color="text.secondary">
                        (−) Principal
                      </Typography>
                    </Box>
                    <Typography variant="body1" sx={{ fontWeight: 600, color: 'error.main' }}>
                      −₹{fmt(principalDue)}
                    </Typography>
                  </Box>

                  {/* Deduction: Interest */}
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      p: 2,
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <ArrowDownward sx={{ color: 'error.main', fontSize: 20 }} />
                      <Typography variant="body2" color="text.secondary">
                        (−) Interest
                      </Typography>
                    </Box>
                    <Typography variant="body1" sx={{ fontWeight: 600, color: 'error.main' }}>
                      −₹{fmt(interestDue)}
                    </Typography>
                  </Box>

                  {/* Deduction: Insurance Premium */}
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      p: 2,
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <ArrowDownward sx={{ color: 'error.main', fontSize: 20 }} />
                      <Typography variant="body2" color="text.secondary">
                        (−) Insurance Premium
                      </Typography>
                    </Box>
                    <Typography variant="body1" sx={{ fontWeight: 600, color: 'error.main' }}>
                      −₹{fmt(insurancePremium)}
                    </Typography>
                  </Box>

                  <Divider />

                  {/* Net: Farmer Profit */}
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      p: 2.5,
                      bgcolor: farmerProfit >= 0 ? '#e8f5e9' : '#ffebee',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <ArrowRight sx={{ color: farmerProfit >= 0 ? 'success.main' : 'error.main', fontSize: 28 }} />
                      <Typography variant="body1" sx={{ fontWeight: 700 }}>
                        = Farmer Profit
                      </Typography>
                    </Box>
                    <Typography
                      variant="h5"
                      sx={{ fontWeight: 700, color: farmerProfit >= 0 ? 'success.main' : 'error.main' }}
                    >
                      ₹{fmt(Math.abs(farmerProfit))}
                    </Typography>
                  </Box>
                </Box>

                {/* Profit / shortfall alert */}
                {saleProceeds > 0 && (
                  <Box sx={{ mt: 2 }}>
                    {farmerProfit > 0 ? (
                      <Alert severity="success" icon={<AttachMoney />} sx={{ borderRadius: 2 }}>
                        <Typography variant="body2">
                          <strong>Profit to be credited:</strong> ₹{fmt(farmerProfit)} will be transferred to your account after settlement.
                        </Typography>
                      </Alert>
                    ) : farmerProfit === 0 ? (
                      <Alert severity="info" sx={{ borderRadius: 2 }}>
                        <Typography variant="body2">
                          Sale proceeds exactly cover all deductions. No shortfall or surplus.
                        </Typography>
                      </Alert>
                    ) : (
                      <Alert severity="error" icon={<Warning />} sx={{ borderRadius: 2 }}>
                        <Typography variant="body2">
                          <strong>Shortfall Alert:</strong> Sale proceeds are ₹{fmt(Math.abs(farmerProfit))} short of covering all deductions. This may trigger a default flag.
                        </Typography>
                      </Alert>
                    )}
                  </Box>
                )}
              </Grid>

              {/* Visual breakdown */}
              <Grid item xs={12} md={5}>
                <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {/* Sale breakdown bars */}
                  <Card variant="outlined" sx={{ borderRadius: 2, p: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                      Deduction Breakdown
                    </Typography>

                    {/* Principal bar */}
                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="caption" color="text.secondary">Principal</Typography>
                        <Typography variant="caption" fontWeight={600}>₹{fmt(principalDue)}</Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={saleProceeds > 0 ? Math.min((principalDue / saleProceeds) * 100, 100) : 0}
                        sx={{
                          height: 8,
                          borderRadius: 4,
                          bgcolor: 'grey.200',
                          '& .MuiLinearProgress-bar': { borderRadius: 4, bgcolor: '#f44336' },
                        }}
                      />
                    </Box>

                    {/* Interest bar */}
                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="caption" color="text.secondary">Interest</Typography>
                        <Typography variant="caption" fontWeight={600}>₹{fmt(interestDue)}</Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={saleProceeds > 0 ? Math.min((interestDue / saleProceeds) * 100, 100) : 0}
                        sx={{
                          height: 8,
                          borderRadius: 4,
                          bgcolor: 'grey.200',
                          '& .MuiLinearProgress-bar': { borderRadius: 4, bgcolor: '#ff9800' },
                        }}
                      />
                    </Box>

                    {/* Insurance bar */}
                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="caption" color="text.secondary">Insurance</Typography>
                        <Typography variant="caption" fontWeight={600}>₹{fmt(insurancePremium)}</Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={saleProceeds > 0 ? Math.min((insurancePremium / saleProceeds) * 100, 100) : 0}
                        sx={{
                          height: 8,
                          borderRadius: 4,
                          bgcolor: 'grey.200',
                          '& .MuiLinearProgress-bar': { borderRadius: 4, bgcolor: '#9c27b0' },
                        }}
                      />
                    </Box>

                    {/* Farmer payout bar */}
                    <Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                          Your Payout
                        </Typography>
                        <Typography variant="caption" fontWeight={700} color="success.main">
                          ₹{fmt(Math.max(farmerProfit, 0))}
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={saleProceeds > 0 ? Math.min((Math.max(farmerProfit, 0) / saleProceeds) * 100, 100) : 0}
                        sx={{
                          height: 8,
                          borderRadius: 4,
                          bgcolor: 'grey.200',
                          '& .MuiLinearProgress-bar': { borderRadius: 4, bgcolor: '#4caf50' },
                        }}
                      />
                    </Box>
                  </Card>

                  {/* Stacked horizontal bar */}
                  <Card variant="outlined" sx={{ borderRadius: 2, p: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                      Proceeds Distribution
                    </Typography>
                    {saleProceeds > 0 ? (
                      <>
                        <Box sx={{ display: 'flex', height: 28, borderRadius: 14, overflow: 'hidden', bgcolor: 'grey.200' }}>
                          <Tooltip title={`Principal: ₹${fmt(principalDue)}`}>
                            <Box sx={{ width: `${(principalDue / saleProceeds) * 100}%`, bgcolor: '#f44336', transition: 'width 0.3s' }} />
                          </Tooltip>
                          <Tooltip title={`Interest: ₹${fmt(interestDue)}`}>
                            <Box sx={{ width: `${(interestDue / saleProceeds) * 100}%`, bgcolor: '#ff9800', transition: 'width 0.3s' }} />
                          </Tooltip>
                          <Tooltip title={`Insurance: ₹${fmt(insurancePremium)}`}>
                            <Box sx={{ width: `${(insurancePremium / saleProceeds) * 100}%`, bgcolor: '#9c27b0', transition: 'width 0.3s' }} />
                          </Tooltip>
                          <Tooltip title={`Farmer Payout: ₹${fmt(Math.max(farmerProfit, 0))}`}>
                            <Box sx={{ width: `${(Math.max(farmerProfit, 0) / saleProceeds) * 100}%`, bgcolor: '#4caf50', transition: 'width 0.3s' }} />
                          </Tooltip>
                        </Box>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mt: 1.5 }}>
                          {[
                            { label: 'Principal', color: '#f44336' },
                            { label: 'Interest', color: '#ff9800' },
                            { label: 'Insurance', color: '#9c27b0' },
                            { label: 'Payout', color: '#4caf50' },
                          ].map((item) => (
                            <Box key={item.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: item.color }} />
                              <Typography variant="caption" color="text.secondary">
                                {item.label}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      </>
                    ) : (
                      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                        No sale proceeds recorded yet.
                      </Typography>
                    )}
                  </Card>

                  {/* Settlement record info */}
                  {latestSettlement && (
                    <Card variant="outlined" sx={{ borderRadius: 2, p: 2, bgcolor: 'grey.50' }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <InfoIcon fontSize="small" color="info" />
                        Settlement Record
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="caption" color="text.secondary">Settlement #</Typography>
                          <Typography variant="caption" fontWeight={600}>
                            {latestSettlement.settlement_number || `#${latestSettlement.id}`}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="caption" color="text.secondary">Date</Typography>
                          <Typography variant="caption" fontWeight={600}>
                            {fmtDate(latestSettlement.settlement_date)}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="caption" color="text.secondary">Gross</Typography>
                          <Typography variant="caption" fontWeight={600}>₹{fmt(latestSettlement.gross_amount)}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="caption" color="text.secondary">Deductions</Typography>
                          <Typography variant="caption" fontWeight={600} color="error.main">
                            −₹{fmt(latestSettlement.deductions)}
                          </Typography>
                        </Box>
                        <Divider sx={{ my: 0.5 }} />
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="caption" sx={{ fontWeight: 600 }}>Net Amount</Typography>
                          <Typography variant="caption" fontWeight={700} color="success.main">
                            ₹{fmt(latestSettlement.net_amount)}
                          </Typography>
                        </Box>
                      </Box>
                    </Card>
                  )}
                </Box>
              </Grid>
            </Grid>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default HarvestSettlementPage;
